import { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { endpoints, formatApiError } from "../../lib/api";
import { formatCurrency } from "../../lib/romEngine";
import { typeMeta, ACCOUNT_TYPES, ACCOUNT_TYPE_META, CONTACT_ROLES, CONTACT_ROLE_LABEL } from "../../lib/accountTypes";
import AccountPicker, { OwnerSelect } from "../../components/AccountPicker";
import { FamilyCard, MergeButton } from "./AccountFamily";
import { ContactHealthBar } from "../../components/ContactHealth";
import { DebriefButton } from "./VisitDebrief";
import { VoiceUpdateButton } from "../../components/VoiceUpdate";
import { CardScanButton } from "../../components/CardScanner";
import {
    ArrowLeft, Buildings, Shield, Plus, X, CircleNotch, Phone, EnvelopeSimple,
    Star, Trash, CalendarCheck, CheckCircle, ChatCircleText, PhoneCall, Envelope,
    Note, Clock, ArrowSquareOut, Microphone, Stop, WarningCircle, FileText,
    ShareNetwork, Scroll, UploadSimple, DownloadSimple,
} from "@phosphor-icons/react";

const STAGES = ["lead", "contacted", "proposal", "negotiation", "won", "active", "closed_lost"];
const STAGE_LABELS = {
    lead: "Lead", contacted: "Contacted", proposal: "Proposal", negotiation: "Negotiation",
    won: "On Supplier List (Won)", active: "Active (Being Invoiced)", closed_lost: "Lost",
};
const ACTIVITY_ICON = { call: PhoneCall, email: Envelope, meeting: CalendarCheck, note: Note, system: ChatCircleText };

export default function AccountDetail() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [data, setData] = useState(null);
    const [err, setErr] = useState("");

    async function load() {
        try { const { data } = await endpoints.clientSummary(id); setData(data); }
        catch (e) { setErr(formatApiError(e)); }
    }
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

    if (err) return <div className="max-w-3xl mx-auto px-6 py-16 text-center text-[var(--rom-danger)]">{err}</div>;
    if (!data) return <div className="max-w-3xl mx-auto px-6 py-16 text-center text-[var(--rom-text-3)]">Loading…</div>;

    const c = data.client;

    async function patch(fields) {
        await endpoints.patchClient(id, fields);
        load();
    }

    return (
        <div className="max-w-[1200px] mx-auto px-6 py-8 space-y-5" data-testid="account-detail-page">
            <button onClick={() => navigate("/crm")} className="text-sm text-[var(--rom-text-2)] hover:text-[var(--rom-text)] flex items-center gap-1.5">
                <ArrowLeft size={14} weight="bold" /> Customer pipeline
            </button>

            <header className="flex flex-wrap items-start justify-between gap-4">
                <div>
                    <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] text-[var(--rom-text-3)] font-semibold mb-1">
                        {(() => { const M = typeMeta(c.type); const I = M.icon; return <><I size={12} weight="bold" /> {M.label}</>; })()}
                        {data.parent && (
                            <> · <button onClick={() => navigate(`/crm/accounts/${data.parent.id}`)} className="normal-case tracking-normal font-semibold text-[var(--rom-navy)] hover:underline" data-testid="account-parent-link">part of {data.parent.name}</button></>
                        )}
                    </div>
                    <h1 className="font-display font-black text-3xl tracking-tight" data-testid="account-name">{c.name}</h1>
                </div>
                <div className="flex items-end gap-2 flex-wrap justify-end">
                <VoiceUpdateButton client={c} onDone={load} />
                <DebriefButton client={c} onDone={load} />
                <MergeButton client={c} onMerged={load} />
                <div>
                    <label className="block text-[10px] uppercase tracking-wider text-[var(--rom-text-3)] font-semibold mb-1">Sales stage</label>
                    <select data-testid="account-stage-select" value={c.stage || "lead"} onChange={(e) => patch({ stage: e.target.value })}
                        className="h-9 px-3 rounded-[4px] border border-[var(--rom-border-strong)] text-sm font-semibold bg-white">
                        {STAGES.map((s) => <option key={s} value={s}>{STAGE_LABELS[s]}</option>)}
                    </select>
                </div>
                </div>
            </header>

            <ContactHealthBar clientId={id} health={data.contact_health} onChanged={load} />

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
                <MiniStat label="Lifetime invoiced" value={formatCurrency(data.lifetime.invoiced)} />
                <MiniStat label="Lifetime paid" value={formatCurrency(data.lifetime.paid)} accent="green" />
                <MiniStat label="Outstanding" value={formatCurrency(data.lifetime.outstanding)} accent={data.lifetime.outstanding > 0 ? "navy" : undefined} />
            </div>
            {data.family_lifetime && (
                <div className="text-xs text-[var(--rom-text-2)] -mt-2" data-testid="family-rollup">
                    Across all {data.family_lifetime.accounts} accounts in this group: invoiced <b>{formatCurrency(data.family_lifetime.invoiced)}</b> · paid <b>{formatCurrency(data.family_lifetime.paid)}</b> · outstanding <b>{formatCurrency(data.family_lifetime.outstanding)}</b>
                </div>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
                <div className="lg:col-span-2 space-y-5">
                    <BasicInfo key={c.updated_at || c.id} c={c} onSave={patch} />
                    <FamilyCard client={c} kids={data.children || []} onChanged={load} navigate={navigate} />
                    <ContactsCard clientId={id} clientName={c.name} contacts={c.contacts || []} onChanged={load} />
                    <NotesCard clientId={id} notes={c.notes_log || []} onChanged={load} />
                    <ActivityCard clientId={id} activities={c.activities || []} onChanged={load} />
                    <ProposalsCard proposals={data.proposals || []} navigate={navigate} />
                    <RelatedCard jobs={data.jobs} projects={data.projects} navigate={navigate} />
                </div>
                <div className="space-y-5">
                    <TasksCard clientId={id} tasks={c.tasks || []} onChanged={load} />
                    <AppointmentsCard clientId={id} appointments={c.appointments || []} onChanged={load} />
                    <ContractsCard clientId={id} clientName={c.name} contracts={data.contracts || []} onChanged={load} />
                </div>
            </div>
        </div>
    );
}

function MiniStat({ label, value, accent }) {
    const color = accent === "green" ? "var(--rom-green)" : accent === "navy" ? "var(--rom-navy)" : "var(--rom-text)";
    return (
        <div className="rom-card"><div className="rom-card-body">
            <div className="text-[10px] uppercase tracking-wider text-[var(--rom-text-3)] font-semibold">{label}</div>
            <div className="font-display font-black text-2xl mt-1" style={{ color }}>{value}</div>
        </div></div>
    );
}

const MAX_RECORDING_SECONDS = 120;

function VoiceNoteButton({ onTranscribed, disabled }) {
    const [recording, setRecording] = useState(false);
    const [busy, setBusy] = useState(false);
    const [busySeconds, setBusySeconds] = useState(0);
    const [seconds, setSeconds] = useState(0);
    const [error, setError] = useState("");
    const recorderRef = useRef(null);
    const chunksRef = useRef([]);
    const streamRef = useRef(null);
    const timerRef = useRef(null);
    const busyTimerRef = useRef(null);

    useEffect(() => () => {
        clearInterval(timerRef.current);
        clearInterval(busyTimerRef.current);
        streamRef.current?.getTracks().forEach((t) => t.stop());
    }, []);

    async function transcribeBlob(blob, isRetry) {
        const formData = new FormData();
        formData.append("file", blob, "voice-note.webm");
        try {
            return await endpoints.transcribeVoice(formData);
        } catch (e) {
            // One silent retry on timeout/network errors — often a transient blip.
            if (!isRetry && (e.code === "ECONNABORTED" || !e.response)) {
                return await transcribeBlob(blob, true);
            }
            throw e;
        }
    }

    async function handleStop() {
        setBusy(true);
        setBusySeconds(0);
        let elapsedBusy = 0;
        busyTimerRef.current = setInterval(() => setBusySeconds(++elapsedBusy), 1000);
        try {
            const blob = new Blob(chunksRef.current, { type: recorderRef.current?.mimeType || "audio/webm" });
            const { data } = await transcribeBlob(blob, false);
            if (data?.text) onTranscribed(data.text);
            else setError("Didn't catch any speech — try again.");
        } catch (e) {
            setError(formatApiError(e) || "Couldn't transcribe that recording.");
        } finally {
            clearInterval(busyTimerRef.current);
            setBusy(false);
        }
    }

    function stop() {
        recorderRef.current?.stop();
        streamRef.current?.getTracks().forEach((t) => t.stop());
        clearInterval(timerRef.current);
        setRecording(false);
    }

    async function start() {
        setError("");
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            streamRef.current = stream;
            const mimeType = window.MediaRecorder?.isTypeSupported?.("audio/webm") ? "audio/webm" : "";
            // Low bitrate — plenty for speech clarity, keeps uploads fast on field/mobile networks.
            const opts = mimeType ? { mimeType, audioBitsPerSecond: 32000 } : { audioBitsPerSecond: 32000 };
            const recorder = new MediaRecorder(stream, opts);
            chunksRef.current = [];
            recorder.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
            recorder.onstop = handleStop;
            recorder.start();
            recorderRef.current = recorder;
            setSeconds(0);
            let elapsed = 0;
            timerRef.current = setInterval(() => {
                elapsed += 1;
                setSeconds(elapsed);
                if (elapsed >= MAX_RECORDING_SECONDS) stop();
            }, 1000);
            setRecording(true);
        } catch (e) {
            setError("Microphone access denied — check your browser permissions.");
        }
    }

    const remaining = Math.max(0, MAX_RECORDING_SECONDS - seconds);
    const mm = Math.floor(remaining / 60);
    const ss = String(remaining % 60).padStart(2, "0");
    const almostUp = remaining <= 10;

    return (
        <div className="flex items-center gap-2 flex-wrap">
            <button type="button" onClick={recording ? stop : start} disabled={disabled || busy}
                data-testid="voice-note-mic-btn" title={recording ? "Stop recording" : "Record a voice note"}
                className={`w-7 h-7 rounded-full flex items-center justify-center border transition-colors shrink-0 ${
                    recording ? "bg-red-600 border-red-600 text-white animate-pulse" : "bg-white border-[var(--rom-border-strong)] text-[var(--rom-navy)] hover:border-[var(--rom-navy)]"
                }`}>
                {busy ? <CircleNotch size={13} className="animate-spin" /> : recording ? <Stop size={12} weight="fill" /> : <Microphone size={13} weight="bold" />}
            </button>
            {recording && (
                <span className={`flex items-center gap-1.5 text-xs font-semibold ${almostUp ? "text-red-700" : "text-red-600"}`} data-testid="voice-note-recording-indicator">
                    <span className="w-2 h-2 rounded-full bg-red-600 animate-pulse" /> Recording… {mm}:{ss} left
                </span>
            )}
            {busy && <span className="text-xs text-[var(--rom-text-2)]" data-testid="voice-note-transcribing">Transcribing… {busySeconds}s</span>}
            {error && (
                <span className="flex items-center gap-1 text-xs text-red-600" data-testid="voice-note-error">
                    <WarningCircle size={12} weight="bold" /> {error}
                </span>
            )}
        </div>
    );
}

function BasicInfo({ c, onSave }) {
    const [form, setForm] = useState({ contact_name: c.contact_name || "", email: c.email || "", phone: c.phone || "", address: c.address || "", notes: c.notes || "",
        type: c.type || "customer", parent_id: c.parent_id || null, owner: c.owner || "", owner_id: c.owner_id || "", website: c.website || "" });
    const [saving, setSaving] = useState(false);
    async function save() { setSaving(true); try { await onSave(form); } finally { setSaving(false); } }
    function appendTranscript(text) {
        const stamp = new Date().toLocaleString(undefined, {
            month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit",
        });
        const entry = `[${stamp}] ${text}`;
        setForm((f) => ({ ...f, notes: f.notes?.trim() ? `${f.notes.trim()}\n${entry}` : entry }));
    }
    return (
        <div className="rom-card" data-testid="account-basic-info">
            <div className="rom-card-head"><h3>Account info</h3></div>
            <div className="rom-card-body grid grid-cols-2 gap-3">
                <div><label className="label">Primary contact (legacy)</label><input className="input" value={form.contact_name} onChange={(e) => setForm((f) => ({ ...f, contact_name: e.target.value }))} data-testid="account-contact-name" /></div>
                <div><label className="label">Email</label><input className="input" value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} data-testid="account-email" /></div>
                <div><label className="label">Phone</label><input className="input" value={form.phone} onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))} data-testid="account-phone" /></div>
                <div><label className="label">Address</label><input className="input" value={form.address} onChange={(e) => setForm((f) => ({ ...f, address: e.target.value }))} data-testid="account-address" /></div>
                <div><label className="label">Account type</label>
                    <select className="input" value={form.type} onChange={(e) => setForm((f) => ({ ...f, type: e.target.value }))} data-testid="account-type-select">
                        {ACCOUNT_TYPES.map((t) => <option key={t} value={t}>{ACCOUNT_TYPE_META[t].label}</option>)}
                    </select>
                </div>
                <div><label className="label">Website</label><input className="input" value={form.website} onChange={(e) => setForm((f) => ({ ...f, website: e.target.value }))} data-testid="account-website" /></div>
                <div><label className="label">Account owner</label>
                    <OwnerSelect ownerId={form.owner_id} ownerName={form.owner} onChange={(o) => setForm((f) => ({ ...f, ...o }))} testId="account-owner-select" />
                </div>
                <div><label className="label">Parent company</label>
                    <AccountPicker value={form.parent_id} excludeId={c.id} onChange={(id) => setForm((f) => ({ ...f, parent_id: id || null }))} placeholder="None — top level" testId="account-parent-picker" />
                </div>
                <div className="col-span-2">
                    <div className="flex items-center justify-between gap-2 mb-1">
                        <label className="label mb-0">Notes</label>
                        <VoiceNoteButton onTranscribed={appendTranscript} />
                    </div>
                    <textarea rows={3} className="input resize-none" value={form.notes} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} data-testid="account-notes" />
                </div>
                <div><button className="btn btn-primary" onClick={save} disabled={saving} data-testid="account-basic-save">{saving ? <CircleNotch size={14} className="animate-spin" /> : "Save"}</button></div>
            </div>
        </div>
    );
}

function ContactsCard({ clientId, clientName, contacts, onChanged }) {
    const [adding, setAdding] = useState(false);
    const [form, setForm] = useState({ name: "", title: "", email: "", phone: "", role: "" });
    const [busy, setBusy] = useState(false);

    async function add() {
        if (!form.name.trim()) return;
        setBusy(true);
        const done = () => { setForm({ name: "", title: "", email: "", phone: "", role: "" }); setAdding(false); onChanged(); };
        try { await endpoints.addClientContact(clientId, form); done(); }
        catch (e) {
            const d = e?.response?.data?.detail;
            if (e?.response?.status === 409 && d?.matches) {
                if (window.confirm(`${d.message}.\n\nAdd them to this account as well?`)) {
                    try { await endpoints.addClientContact(clientId, { ...form, force: true }); done(); }
                    catch (e2) { alert(formatApiError(e2)); }
                }
            } else alert(formatApiError(e));
        }
        finally { setBusy(false); }
    }
    async function setRole(cid, role) { await endpoints.updateClientContact(clientId, cid, { role }); onChanged(); }
    async function makePrimary(cid) { await endpoints.updateClientContact(clientId, cid, { is_primary: true }); onChanged(); }
    async function remove(cid) { await endpoints.deleteClientContact(clientId, cid); onChanged(); }

    return (
        <div className="rom-card" data-testid="contacts-card">
            <div className="rom-card-head"><h3>Contacts</h3><div className="flex items-center gap-1"><CardScanButton small account={{ id: clientId, name: clientName }} onDone={onChanged} /><button onClick={() => setAdding((a) => !a)} className="btn btn-ghost btn-sm" data-testid="contact-add-toggle"><Plus size={12} weight="bold" /> Add contact</button></div></div>
            <div className="rom-card-body space-y-2">
                {adding && (
                    <div className="grid grid-cols-2 gap-2 p-3 border border-dashed border-[var(--rom-border-strong)] rounded-[6px]" data-testid="contact-add-form">
                        <input className="input" placeholder="Name" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} data-testid="contact-name-input" />
                        <input className="input" placeholder="Title" value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} />
                        <input className="input" placeholder="Email" value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} />
                        <input className="input" placeholder="Phone" value={form.phone} onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))} />
                        <select className="input col-span-2" value={form.role} onChange={(e) => setForm((f) => ({ ...f, role: e.target.value }))} data-testid="contact-role-select">
                            {CONTACT_ROLES.map(([v, l]) => <option key={v} value={v}>{v ? l : "Role (optional)"}</option>)}
                        </select>
                        <button className="btn btn-primary btn-sm col-span-2 justify-center" onClick={add} disabled={busy} data-testid="contact-save-btn">{busy ? <CircleNotch size={13} className="animate-spin" /> : "Save contact"}</button>
                    </div>
                )}
                {contacts.length === 0 ? <div className="text-sm text-[var(--rom-text-3)] text-center py-4">No contacts yet.</div> : contacts.map((ct) => (
                    <div key={ct.id} className="flex items-center justify-between gap-2 p-2.5 border border-[var(--rom-border)] rounded-[4px]" data-testid={`contact-row-${ct.id}`}>
                        <div>
                            <div className="font-semibold text-sm flex items-center gap-1.5">{ct.name} {ct.is_primary && <Star size={12} weight="fill" className="text-[var(--rom-orange)]" />}{ct.title && <span className="text-[var(--rom-text-3)] font-normal">· {ct.title}</span>}{ct.role && <span className="chip chip-gray text-[10px] font-semibold" data-testid={`contact-role-${ct.id}`}>{CONTACT_ROLE_LABEL[ct.role] || ct.role}</span>}</div>
                            <div className="text-xs text-[var(--rom-text-2)] flex items-center gap-3 mt-0.5">
                                {ct.email && <span className="flex items-center gap-1"><EnvelopeSimple size={11} /> {ct.email}</span>}
                                {ct.phone && <span className="flex items-center gap-1"><Phone size={11} /> {ct.phone}</span>}
                            </div>
                        </div>
                        <div className="flex items-center gap-1">
                            <select value={ct.role || ""} onChange={(e) => setRole(ct.id, e.target.value)} title="Role"
                                className="h-7 px-1.5 rounded-[4px] border border-[var(--rom-border)] text-[11px] bg-white text-[var(--rom-text-2)]" data-testid={`contact-role-edit-${ct.id}`}>
                                {CONTACT_ROLES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                            </select>
                            {!ct.is_primary && <button onClick={() => makePrimary(ct.id)} className="btn btn-ghost btn-sm" title="Make primary" data-testid={`contact-make-primary-${ct.id}`}><Star size={12} /></button>}
                            <button onClick={() => remove(ct.id)} className="btn btn-ghost btn-sm text-red-600" data-testid={`contact-delete-${ct.id}`}><Trash size={12} /></button>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}

function ActivityCard({ clientId, activities, onChanged }) {
    const [adding, setAdding] = useState(false);
    const [form, setForm] = useState({ type: "call", subject: "", body: "" });
    const [busy, setBusy] = useState(false);

    async function add() {
        if (!form.subject.trim()) return;
        setBusy(true);
        try { await endpoints.addClientActivity(clientId, form); setForm({ type: "call", subject: "", body: "" }); setAdding(false); onChanged(); }
        catch (e) { alert(formatApiError(e)); }
        finally { setBusy(false); }
    }

    return (
        <div className="rom-card" data-testid="activity-card">
            <div className="rom-card-head"><h3>Activity</h3><button onClick={() => setAdding((a) => !a)} className="btn btn-ghost btn-sm" data-testid="activity-add-toggle"><Plus size={12} weight="bold" /> Log activity</button></div>
            <div className="rom-card-body space-y-2">
                {adding && (
                    <div className="space-y-2 p-3 border border-dashed border-[var(--rom-border-strong)] rounded-[6px]" data-testid="activity-add-form">
                        <div className="flex gap-2">
                            {["call", "email", "meeting", "note"].map((t) => (
                                <button key={t} onClick={() => setForm((f) => ({ ...f, type: t }))} data-testid={`activity-type-${t}`}
                                    className={`px-2.5 py-1 text-xs font-semibold rounded border capitalize ${form.type === t ? "bg-[var(--rom-navy)] text-white border-[var(--rom-navy)]" : "bg-white border-[var(--rom-border-strong)]"}`}>{t}</button>
                            ))}
                        </div>
                        <input className="input" placeholder="Subject" value={form.subject} onChange={(e) => setForm((f) => ({ ...f, subject: e.target.value }))} data-testid="activity-subject-input" />
                        <textarea className="input resize-none" rows={2} placeholder="Notes (optional)" value={form.body} onChange={(e) => setForm((f) => ({ ...f, body: e.target.value }))} />
                        <button className="btn btn-primary btn-sm" onClick={add} disabled={busy} data-testid="activity-save-btn">{busy ? <CircleNotch size={13} className="animate-spin" /> : "Save"}</button>
                    </div>
                )}
                {activities.length === 0 ? <div className="text-sm text-[var(--rom-text-3)] text-center py-4">No activity logged yet.</div> : (
                    <div className="space-y-2 max-h-[420px] overflow-y-auto">
                        {activities.map((a) => {
                            const Icon = ACTIVITY_ICON[a.type] || Note;
                            return (
                                <div key={a.id} className="flex items-start gap-2.5 p-2 border-b border-[var(--rom-border)] last:border-0" data-testid={`activity-row-${a.id}`}>
                                    <Icon size={14} weight="bold" className="text-[var(--rom-navy)] mt-0.5 shrink-0" />
                                    <div className="flex-1">
                                        <div className="text-sm font-medium">{a.subject}</div>
                                        {a.body && <div className="text-xs text-[var(--rom-text-2)] mt-0.5 whitespace-pre-wrap">{a.body}</div>}
                                        <div className="text-[10px] text-[var(--rom-text-3)] mt-0.5">{a.created_by ? `${a.created_by} · ` : ""}{fmtDate(a.created_at)}</div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        </div>
    );
}

function NotesCard({ clientId, notes, onChanged }) {
    const [body, setBody] = useState("");
    const [busy, setBusy] = useState(false);

    async function add() {
        if (!body.trim()) return;
        setBusy(true);
        try { await endpoints.addClientNote(clientId, { body }); setBody(""); onChanged(); }
        catch (e) { alert(formatApiError(e)); }
        finally { setBusy(false); }
    }
    async function remove(noteId) {
        try { await endpoints.deleteClientNote(clientId, noteId); onChanged(); }
        catch (e) { alert(formatApiError(e)); }
    }

    return (
        <div className="rom-card" data-testid="notes-card">
            <div className="rom-card-head"><h3>Notes</h3></div>
            <div className="rom-card-body space-y-3">
                <div className="flex items-start gap-2">
                    <textarea rows={2} className="input resize-none flex-1" placeholder="Add a note about this account…" value={body}
                        onChange={(e) => setBody(e.target.value)} data-testid="note-input" />
                    <div className="flex flex-col items-center gap-1.5 shrink-0 pt-1">
                        <VoiceNoteButton onTranscribed={(t) => setBody((b) => b.trim() ? `${b.trim()}\n${t}` : t)} />
                        <button className="btn btn-primary btn-sm" onClick={add} disabled={busy || !body.trim()} data-testid="note-save-btn">
                            {busy ? <CircleNotch size={13} className="animate-spin" /> : "Save"}
                        </button>
                    </div>
                </div>
                {notes.length === 0 ? <div className="text-sm text-[var(--rom-text-3)] text-center py-4">No notes yet.</div> : (
                    <div className="space-y-2 max-h-[340px] overflow-y-auto">
                        {notes.map((n) => (
                            <div key={n.id} className="flex items-start justify-between gap-2 p-2.5 border border-[var(--rom-border)] rounded-[4px]" data-testid={`note-row-${n.id}`}>
                                <div className="min-w-0">
                                    <div className="text-sm whitespace-pre-wrap">{n.body}</div>
                                    <div className="text-[10px] text-[var(--rom-text-3)] mt-1">{n.created_by ? `${n.created_by} · ` : ""}{fmtDate(n.created_at)}</div>
                                </div>
                                <button onClick={() => remove(n.id)} className="btn btn-ghost btn-sm text-red-600 shrink-0" data-testid={`note-delete-${n.id}`}><Trash size={12} /></button>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}

function AppointmentsCard({ clientId, appointments, onChanged }) {
    const [adding, setAdding] = useState(false);
    const [form, setForm] = useState({ date: "", time: "", notes: "" });
    const [busy, setBusy] = useState(false);

    async function add() {
        if (!form.date) return;
        setBusy(true);
        try { await endpoints.addClientAppointment(clientId, form); setForm({ date: "", time: "", notes: "" }); setAdding(false); onChanged(); }
        catch (e) { alert(formatApiError(e)); }
        finally { setBusy(false); }
    }
    async function setStatus(id, status) { await endpoints.updateClientAppointment(clientId, id, { status }); onChanged(); }

    return (
        <div className="rom-card" data-testid="appointments-card">
            <div className="rom-card-head"><h3>Follow-ups</h3><button onClick={() => setAdding((a) => !a)} className="btn btn-ghost btn-sm" data-testid="appointment-add-toggle"><Plus size={12} weight="bold" /> Schedule</button></div>
            <div className="rom-card-body space-y-2">
                <p className="text-[11px] text-[var(--rom-text-3)] -mt-1">In-app only — not synced to a calendar yet.</p>
                {adding && (
                    <div className="space-y-2 p-3 border border-dashed border-[var(--rom-border-strong)] rounded-[6px]" data-testid="appointment-add-form">
                        <div className="grid grid-cols-2 gap-2">
                            <input type="date" className="input" value={form.date} onChange={(e) => setForm((f) => ({ ...f, date: e.target.value }))} data-testid="appointment-date-input" />
                            <input type="time" className="input" value={form.time} onChange={(e) => setForm((f) => ({ ...f, time: e.target.value }))} />
                        </div>
                        <input className="input" placeholder="Notes (e.g. site walk)" value={form.notes} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} />
                        <button className="btn btn-primary btn-sm" onClick={add} disabled={busy} data-testid="appointment-save-btn">{busy ? <CircleNotch size={13} className="animate-spin" /> : "Save"}</button>
                    </div>
                )}
                {appointments.length === 0 ? <div className="text-sm text-[var(--rom-text-3)] text-center py-4">Nothing scheduled.</div> : appointments.map((a) => (
                    <div key={a.id} className={`p-2.5 border rounded-[4px] ${a.status === "done" ? "opacity-50 border-[var(--rom-border)]" : "border-[var(--rom-border)]"}`} data-testid={`appointment-row-${a.id}`}>
                        <div className="flex items-center justify-between">
                            <div className="text-sm font-semibold flex items-center gap-1.5"><CalendarCheck size={13} weight="bold" className="text-[var(--rom-navy)]" /> {a.date}{a.time ? ` · ${a.time}` : ""}</div>
                            {a.status === "scheduled" && (
                                <div className="flex gap-1">
                                    <button onClick={() => setStatus(a.id, "done")} className="text-[var(--rom-green)]" title="Mark done" data-testid={`appointment-done-${a.id}`}><CheckCircle size={14} weight="bold" /></button>
                                    <button onClick={() => setStatus(a.id, "cancelled")} className="text-red-500" title="Cancel" data-testid={`appointment-cancel-${a.id}`}><X size={14} weight="bold" /></button>
                                </div>
                            )}
                        </div>
                        {a.notes && <div className="text-xs text-[var(--rom-text-2)] mt-1">{a.notes}</div>}
                        {a.owner && <div className="text-[10px] text-[var(--rom-text-3)] mt-0.5">{a.owner}</div>}
                    </div>
                ))}
            </div>
        </div>
    );
}

function dueBadge(dueDate, status) {
    if (status === "done") return { label: "Completed", color: "#00853F", bg: "rgba(0,133,63,0.1)" };
    if (!dueDate) return { label: "No due date", color: "#94a3b8", bg: "rgba(148,163,184,0.12)" };
    const today = new Date(); today.setHours(0, 0, 0, 0);
    const due = new Date(`${dueDate}T00:00:00`);
    const diffDays = Math.round((due - today) / 86400000);
    if (diffDays < 0) return { label: `Overdue ${Math.abs(diffDays)}d`, color: "#dc2626", bg: "rgba(220,38,38,0.1)" };
    if (diffDays === 0) return { label: "Due today", color: "#d97706", bg: "rgba(217,119,6,0.12)" };
    if (diffDays <= 3) return { label: `Due in ${diffDays}d`, color: "#d97706", bg: "rgba(217,119,6,0.12)" };
    return { label: `Due in ${diffDays}d`, color: "#00853F", bg: "rgba(0,133,63,0.1)" };
}

function TasksCard({ clientId, tasks, onChanged }) {
    const [adding, setAdding] = useState(false);
    const [form, setForm] = useState({ title: "", assigned_to: "", next_step: "", due_date: "" });
    const [busy, setBusy] = useState(false);

    async function add() {
        if (!form.title.trim()) return;
        setBusy(true);
        try { await endpoints.addClientTask(clientId, form); setForm({ title: "", assigned_to: "", next_step: "", due_date: "" }); setAdding(false); onChanged(); }
        catch (e) { alert(formatApiError(e)); }
        finally { setBusy(false); }
    }
    async function toggle(t) { await endpoints.updateClientTask(clientId, t.id, { status: t.status === "open" ? "done" : "open" }); onChanged(); }
    async function remove(id) { await endpoints.deleteClientTask(clientId, id); onChanged(); }

    const sorted = [...tasks].sort((a, b) => {
        if (a.status !== b.status) return a.status === "open" ? -1 : 1;
        if (!a.due_date) return 1;
        if (!b.due_date) return -1;
        return a.due_date.localeCompare(b.due_date);
    });

    return (
        <div className="rom-card" data-testid="tasks-card">
            <div className="rom-card-head"><h3>Actions / next steps</h3><button onClick={() => setAdding((a) => !a)} className="btn btn-ghost btn-sm" data-testid="task-add-toggle"><Plus size={12} weight="bold" /> Add</button></div>
            <div className="rom-card-body space-y-2">
                {adding && (
                    <div className="space-y-2 p-3 border border-dashed border-[var(--rom-border-strong)] rounded-[6px]" data-testid="task-add-form">
                        <input className="input" placeholder="Task (e.g. Book follow up)" value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} data-testid="task-title-input" />
                        <input className="input" placeholder="Assigned to (e.g. John)" value={form.assigned_to} onChange={(e) => setForm((f) => ({ ...f, assigned_to: e.target.value }))} data-testid="task-assigned-input" />
                        <input className="input" placeholder="Next step (e.g. John to demo)" value={form.next_step} onChange={(e) => setForm((f) => ({ ...f, next_step: e.target.value }))} />
                        <input type="date" className="input" value={form.due_date} onChange={(e) => setForm((f) => ({ ...f, due_date: e.target.value }))} />
                        <button className="btn btn-primary btn-sm" onClick={add} disabled={busy} data-testid="task-save-btn">{busy ? <CircleNotch size={13} className="animate-spin" /> : "Save"}</button>
                    </div>
                )}
                {tasks.length === 0 ? <div className="text-sm text-[var(--rom-text-3)] text-center py-4">No actions yet.</div> : sorted.map((t) => {
                    const badge = dueBadge(t.due_date, t.status);
                    return (
                        <div key={t.id} className="flex items-start justify-between gap-2 p-2.5 border border-[var(--rom-border)] rounded-[4px]" data-testid={`task-row-${t.id}`}>
                            <div className="flex items-start gap-2 min-w-0">
                                <button onClick={() => toggle(t)} data-testid={`task-toggle-${t.id}`} title={t.status === "done" ? "Reopen" : "Mark complete"} className="mt-0.5 shrink-0">
                                    <CheckCircle size={18} weight={t.status === "done" ? "fill" : "regular"} className={t.status === "done" ? "text-[var(--rom-green)]" : "text-[var(--rom-text-3)] hover:text-[var(--rom-navy)]"} />
                                </button>
                                <div className={`min-w-0 ${t.status === "done" ? "line-through text-[var(--rom-text-3)]" : ""}`}>
                                    <div className="text-sm font-medium">{t.title}</div>
                                    {t.next_step && <div className="text-xs text-[var(--rom-text-2)]">{t.next_step}</div>}
                                    <div className="flex items-center gap-2 mt-1 flex-wrap">
                                        {t.assigned_to && <span className="text-[10px] text-[var(--rom-text-3)]">{t.assigned_to}</span>}
                                        <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full" style={{ color: badge.color, background: badge.bg }} data-testid={`task-due-badge-${t.id}`}>{badge.label}</span>
                                    </div>
                                </div>
                            </div>
                            <div className="flex items-center gap-1.5 shrink-0">
                                {t.status === "open" ? (
                                    <button onClick={() => toggle(t)} className="btn btn-sm" style={{ color: "#00853F", borderColor: "#00853F" }} data-testid={`task-complete-btn-${t.id}`}>Complete</button>
                                ) : (
                                    <button onClick={() => toggle(t)} className="text-[10px] font-semibold text-[var(--rom-text-3)] hover:text-[var(--rom-navy)]" data-testid={`task-reopen-btn-${t.id}`}>Reopen</button>
                                )}
                                <button onClick={() => remove(t.id)} className="text-[var(--rom-text-3)] hover:text-red-600" data-testid={`task-delete-${t.id}`}><Trash size={12} /></button>
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

const PROPOSAL_STATUS = {
    draft: { cls: "chip-gray", label: "Draft" },
    sent: { cls: "chip-orange", label: "Sent" },
    viewed: { cls: "chip-navy", label: "Viewed" },
    changes_requested: { cls: "chip-red", label: "Changes requested" },
    signed: { cls: "chip-green", label: "Signed" },
    accepted: { cls: "chip-green", label: "Accepted" },
};

function ProposalsCard({ proposals, navigate }) {
    return (
        <div className="rom-card" data-testid="proposals-card">
            <div className="rom-card-head"><h3>Proposals</h3></div>
            <div className="rom-card-body space-y-2">
                {proposals.length === 0 ? <div className="text-sm text-[var(--rom-text-3)] text-center py-4">No proposals sent to this account yet.</div> : proposals.map((p) => {
                    const meta = PROPOSAL_STATUS[p.status] || PROPOSAL_STATUS.draft;
                    return (
                        <button key={p.id} onClick={() => navigate(`/calculator/${p.job_id}`)}
                            className="w-full flex items-center justify-between gap-3 p-2.5 border border-[var(--rom-border)] rounded-[4px] hover:border-[var(--rom-navy)] text-left"
                            data-testid={`proposal-row-${p.id}`}>
                            <div className="min-w-0">
                                <div className="font-semibold text-sm font-mono flex items-center gap-2">
                                    <FileText size={13} weight="bold" className="text-[var(--rom-text-3)]" /> {p.ref || "ROM"}
                                </div>
                                <div className="text-[11px] text-[var(--rom-text-3)] mt-0.5">{p.recipient_name || "—"} · {fmtDate(p.updated_at)}</div>
                            </div>
                            <div className="flex items-center gap-2 shrink-0">
                                <span className="font-mono font-bold text-sm">{formatCurrency(p.grand_total)}</span>
                                <span className={`chip ${meta.cls}`}>{meta.label}</span>
                            </div>
                        </button>
                    );
                })}
            </div>
        </div>
    );
}

const CONTRACT_TYPE_LABEL = { msa: "MSA", nda: "NDA", other: "Other" };
const CONTRACT_STATUS_META = {
    draft: { cls: "chip-gray", label: "Draft" },
    sent: { cls: "chip-orange", label: "Sent" },
    signed: { cls: "chip-green", label: "Signed" },
    expired: { cls: "chip-red", label: "Expired" },
};

function ContractsCard({ clientId, clientName, contracts, onChanged }) {
    const [adding, setAdding] = useState(false);
    const [form, setForm] = useState({ title: `Master Service Agreement — ${clientName}`, type: "msa", status: "draft", effective_date: "", expiry_date: "", notes: "" });
    const [file, setFile] = useState(null);
    const [busy, setBusy] = useState(false);
    const [uploadingId, setUploadingId] = useState(null);

    async function add() {
        if (!form.title.trim()) return;
        setBusy(true);
        try {
            const { data: contract } = await endpoints.createContract({ ...form, client_id: clientId });
            if (file) {
                const fd = new FormData();
                fd.append("file", file);
                fd.append("kind", "contract");
                const { data: uploaded } = await endpoints.uploadFile(fd);
                await endpoints.updateContract(contract.id, { file_id: uploaded.id, file_name: uploaded.original_filename || file.name, file_size: uploaded.size });
            }
            setForm({ title: `Master Service Agreement — ${clientName}`, type: "msa", status: "draft", effective_date: "", expiry_date: "", notes: "" });
            setFile(null);
            setAdding(false);
            onChanged();
        } catch (e) { alert(formatApiError(e)); }
        finally { setBusy(false); }
    }

    async function setStatus(contractId, status) {
        try { await endpoints.updateContract(contractId, { status }); onChanged(); }
        catch (e) { alert(formatApiError(e)); }
    }

    async function attachFile(contractId, f) {
        setUploadingId(contractId);
        try {
            const fd = new FormData();
            fd.append("file", f);
            fd.append("kind", "contract");
            const { data: uploaded } = await endpoints.uploadFile(fd);
            await endpoints.updateContract(contractId, { file_id: uploaded.id, file_name: uploaded.original_filename || f.name, file_size: uploaded.size });
            onChanged();
        } catch (e) { alert(formatApiError(e)); }
        finally { setUploadingId(null); }
    }

    async function remove(contractId) {
        if (!window.confirm("Delete this contract record?")) return;
        try { await endpoints.deleteContract(contractId); onChanged(); }
        catch (e) { alert(formatApiError(e)); }
    }

    return (
        <div className="rom-card" data-testid="contracts-card">
            <div className="rom-card-head"><h3>Contracts</h3><button onClick={() => setAdding((a) => !a)} className="btn btn-ghost btn-sm" data-testid="contract-add-toggle"><Plus size={12} weight="bold" /> Add contract</button></div>
            <div className="rom-card-body space-y-2">
                {adding && (
                    <div className="space-y-2 p-3 border border-dashed border-[var(--rom-border-strong)] rounded-[6px]" data-testid="contract-add-form">
                        <input className="input" placeholder="Title" value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} data-testid="contract-title-input" />
                        <div className="grid grid-cols-2 gap-2">
                            <select className="input" value={form.type} onChange={(e) => setForm((f) => ({ ...f, type: e.target.value }))} data-testid="contract-type-select">
                                <option value="msa">MSA</option><option value="nda">NDA</option><option value="other">Other</option>
                            </select>
                            <select className="input" value={form.status} onChange={(e) => setForm((f) => ({ ...f, status: e.target.value }))} data-testid="contract-status-select">
                                <option value="draft">Draft</option><option value="sent">Sent</option><option value="signed">Signed</option><option value="expired">Expired</option>
                            </select>
                            <div><label className="label">Effective date</label><input type="date" className="input" value={form.effective_date} onChange={(e) => setForm((f) => ({ ...f, effective_date: e.target.value }))} /></div>
                            <div><label className="label">Expiry date</label><input type="date" className="input" value={form.expiry_date} onChange={(e) => setForm((f) => ({ ...f, expiry_date: e.target.value }))} /></div>
                        </div>
                        <textarea className="input resize-none" rows={2} placeholder="Notes (optional)" value={form.notes} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} />
                        <label className="flex items-center gap-2 text-xs text-[var(--rom-text-2)] cursor-pointer">
                            <UploadSimple size={14} weight="bold" /> {file ? file.name : "Attach file (optional)"}
                            <input type="file" className="hidden" accept=".pdf,.doc,.docx" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="contract-file-input" />
                        </label>
                        <button className="btn btn-primary btn-sm" onClick={add} disabled={busy} data-testid="contract-save-btn">{busy ? <CircleNotch size={13} className="animate-spin" /> : "Save contract"}</button>
                    </div>
                )}
                {contracts.length === 0 ? <div className="text-sm text-[var(--rom-text-3)] text-center py-4">No contracts on file.</div> : contracts.map((ct) => {
                    const meta = CONTRACT_STATUS_META[ct.status] || CONTRACT_STATUS_META.draft;
                    return (
                        <div key={ct.id} className="p-2.5 border border-[var(--rom-border)] rounded-[4px]" data-testid={`contract-row-${ct.id}`}>
                            <div className="flex items-start justify-between gap-2">
                                <div className="min-w-0 flex items-center gap-1.5">
                                    <Scroll size={13} weight="bold" className="text-[var(--rom-text-3)] shrink-0" />
                                    <div className="font-semibold text-sm truncate">{ct.title}</div>
                                </div>
                                <button onClick={() => remove(ct.id)} className="btn btn-ghost btn-sm text-red-600 shrink-0" data-testid={`contract-delete-${ct.id}`}><Trash size={12} /></button>
                            </div>
                            <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                                <span className="chip chip-gray">{CONTRACT_TYPE_LABEL[ct.type] || "Other"}</span>
                                <select value={ct.status} onChange={(e) => setStatus(ct.id, e.target.value)} data-testid={`contract-status-change-${ct.id}`}
                                    className={`chip ${meta.cls}`} style={{ border: "none", cursor: "pointer" }}>
                                    <option value="draft">Draft</option><option value="sent">Sent</option><option value="signed">Signed</option><option value="expired">Expired</option>
                                </select>
                                {(ct.effective_date || ct.expiry_date) && (
                                    <span className="text-[11px] text-[var(--rom-text-3)]">{ct.effective_date || "—"} → {ct.expiry_date || "—"}</span>
                                )}
                            </div>
                            <div className="mt-1.5">
                                {ct.file_id ? (
                                    <a href={endpoints.fileDownloadUrl(ct.file_id)} target="_blank" rel="noreferrer" className="text-xs font-semibold text-[var(--rom-navy)] hover:underline flex items-center gap-1 w-fit" data-testid={`contract-view-file-${ct.id}`}>
                                        <DownloadSimple size={12} weight="bold" /> {ct.file_name || "View file"}
                                    </a>
                                ) : (
                                    <label className="text-xs font-semibold text-[var(--rom-text-2)] hover:text-[var(--rom-navy)] flex items-center gap-1 w-fit cursor-pointer">
                                        {uploadingId === ct.id ? <CircleNotch size={12} className="animate-spin" /> : <UploadSimple size={12} weight="bold" />} Attach file
                                        <input type="file" className="hidden" accept=".pdf,.doc,.docx" onChange={(e) => e.target.files?.[0] && attachFile(ct.id, e.target.files[0])} data-testid={`contract-upload-${ct.id}`} />
                                    </label>
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

function RelatedCard({ jobs, projects, navigate }) {
    return (
        <div className="rom-card" data-testid="related-card">
            <div className="rom-card-head"><h3>Related jobs &amp; projects</h3></div>
            <div className="rom-card-body space-y-3">
                {projects.length === 0 && jobs.length === 0 ? (
                    <div className="text-sm text-[var(--rom-text-3)] text-center py-4">Nothing linked to this account yet.</div>
                ) : (
                    <>
                        {projects.map((p) => (
                            <button key={p.id} onClick={() => navigate(`/projects/${p.id}`)} className="w-full flex items-center justify-between p-2.5 border border-[var(--rom-border)] rounded-[4px] hover:border-[var(--rom-navy)] text-left" data-testid={`related-project-${p.id}`}>
                                <div>
                                    <div className="font-semibold text-sm">{p.name}</div>
                                    <div className="text-[11px] text-[var(--rom-text-3)] font-mono">{p.project_number} · {p.stage}</div>
                                </div>
                                <div className="text-right">
                                    <div className="font-mono font-bold text-sm">{formatCurrency(p.contract_value)}</div>
                                    <ArrowSquareOut size={12} className="text-[var(--rom-text-3)] inline" />
                                </div>
                            </button>
                        ))}
                        {jobs.filter((j) => !projects.some((p) => p.id === j.id)).map((j) => (
                            <button key={j.id} onClick={() => navigate(`/calculator/${j.id}`)} className="w-full flex items-center justify-between p-2.5 border border-[var(--rom-border)] rounded-[4px] hover:border-[var(--rom-navy)] text-left" data-testid={`related-job-${j.id}`}>
                                <div>
                                    <div className="font-semibold text-sm font-mono">{j.ref}</div>
                                    <div className="text-[11px] text-[var(--rom-text-3)] capitalize">{j.loss_type} · {j.status}</div>
                                </div>
                                <div className="font-mono font-bold text-sm">{formatCurrency(j.grand_total)}</div>
                            </button>
                        ))}
                    </>
                )}
            </div>
        </div>
    );
}

function fmtDate(s) { try { return new Date(s).toLocaleString(); } catch { return s; } }
